import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import FeedbackForm from "./FeedbackForm";

const StudentExamPage = () => {
  const { examId, userId } = useParams();
  const [backendUrl, setBackendUrl] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [examStarted, setExamStarted] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionStatuses, setQuestionStatuses] = useState({});
  const [markForReview, setMarkForReview] = useState({});
  //const [showFeedback, setShowFeedback] = useState(false);
  const [showFeedback, setShowFeedback] = useState(null);

  //const [attemptNo, setAttemptNo] = useState(null);

  const cheatingEnabled = useRef(false);
  const isAutoSubmitting = useRef(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/config.js";
    script.onload = () => {
      if (window.env?.BACKEND_URL) setBackendUrl(window.env.BACKEND_URL);
    };
    script.onerror = () => console.error("Failed to load config.js");
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!examStarted) return;
    cheatingEnabled.current = true;

    let violationCount = 0;
    let hasWarned = false;

    const handleViolation = (msg) => {
      if (!hasWarned) {
        violationCount++;
        if (violationCount <= 5) {
          alert(`⚠️ Warning\n\n${msg}`);
        } else {
          alert("Maximum warnings reached. Exam will auto-submit.");
          handleAutoSubmit();
        }
        hasWarned = true;
      }
    };

    const handleVisibility = () => {
      if (cheatingEnabled.current && document.visibilityState === "hidden") {
        handleViolation("Switching tabs is not allowed.");
      } else hasWarned = false;
    };

    const handleBlur = () => {
      if (cheatingEnabled.current && !hasWarned) {
        handleViolation("Switching windows is not allowed.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("contextmenu", (e) => e.preventDefault());
    document.addEventListener("keydown", (e) => {
      const blocked = ["c", "x", "v", "a", "r"];
      if (e.ctrlKey && blocked.includes(e.key.toLowerCase())) e.preventDefault();
    });

    if (sessionStorage.getItem("examStarted")) {
      alert("⚠️ Multiple tabs detected! Only one allowed.");
      window.close();
    } else sessionStorage.setItem("examStarted", "true");

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
      sessionStorage.removeItem("examStarted");
    };
  }, [examStarted]);

  useEffect(() => {
    if (backendUrl) fetchRemainingAttempts();
  }, [backendUrl]);

  const fetchRemainingAttempts = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/student/exams/${examId}/attempts/${parseInt(userId)}`
      );
      setRemainingAttempts(res.data.remainingAttempts);
      return res.data.remainingAttempts;
    } catch {
      setRemainingAttempts(null);
      return null;
    }
  };

  const startExam = async () => {
    if (!backendUrl) return;

    try {
      if (document.documentElement.requestFullscreen)
        document.documentElement.requestFullscreen().catch(() => {});

      const res = await axios.post(
        `${backendUrl}/student-exam/${examId}/start`,
        { studentId: parseInt(userId) }
      );

      const { attemptId, questions, timeLimit } = res.data;
  
      if (!questions?.length) return alert("⚠️ No questions found.");

      setQuestions(questions);
      setAnswers({});
      setTimeLeft(timeLimit * 60);
      setAttemptId(attemptId);
      setCurrentIndex(0);
      setExamStarted(true);

      const init = {};
      questions.forEach((q) => (init[q.Question_ID] = "notVisited"));
      setQuestionStatuses(init);
    } catch (err) {
      console.error(err);
      alert("Failed to start exam");
    }
  };

  useEffect(() => {
    if (!examStarted || timeLeft <= 0) return;
    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [examStarted, timeLeft]);

  const handleOptionChange = (qId, ansId) => {
    setAnswers((p) => ({ ...p, [qId]: ansId }));
    setQuestionStatuses((p) => ({ ...p, [qId]: "answered" }));
  };

  const handleMarkForReview = (qId) => {
    setMarkForReview((p) => ({ ...p, [qId]: !p[qId] }));
    setQuestionStatuses((p) => ({
      ...p,
      [qId]: !markForReview[qId] ? "review" : "answered",
    }));
  };

  const handleAutoSubmit = async () => {
    isAutoSubmitting.current = true;
    cheatingEnabled.current = false;
    await handleSubmit();
  };

  const submitExam = async () => {
    if (!window.confirm("⚠️ Are you sure you want to submit?")) return;
    isAutoSubmitting.current = false;
    cheatingEnabled.current = false;
    setTimeLeft(0);
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (!backendUrl) return;

    try {
      await axios.post(`${backendUrl}/student-exam/submit`, {
        attemptId,
        answers,
      });

      alert(
        isAutoSubmitting.current
          ? "🕒 Time up! Exam submitted."
          : "✅ Exam submitted!"
      );

      const newRemainingAttempts = await fetchRemainingAttempts();

     if (newRemainingAttempts === 0) {
        setShowFeedback({
         examId: examId,
         userId: userId
     });
  }

      setAnswers({});
      setQuestions([]);
      setAttemptId(null);
      setTimeLeft(0);
      setExamStarted(false);
      isAutoSubmitting.current = false;
    } catch (err) {
      console.error(err);
      alert("Submission failed");
    }
  };

  const formatTime = (s) =>
    `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;

  const answeredCount = Object.values(questionStatuses).filter(
    (s) => s === "answered"
  ).length;

  const reviewCount = Object.values(questionStatuses).filter(
    (s) => s === "review"
  ).length;

  const notVisitedCount = Object.values(questionStatuses).filter(
    (s) => s === "notVisited"
  ).length;

  const attemptedCount = answeredCount + reviewCount;

  // ===========================================================
  // ✅ FEEDBACK FORM UI 
  // ===========================================================
  if (showFeedback) {
    return (
      <FeedbackForm
        examId={examId}
        //attemptNo={attemptNo}
        userId={userId}
        onSubmitComplete={() => {
          alert("Feedback submitted. Redirecting...");
          window.location.href = "/student-dashboard";
        }}
      />
    );
  }
  // ===========================================================
  //  EXAM UI
  // ===========================================================

  return (
        <div style={styles.container}>
        <h2 style={styles.title}>Online Assessment</h2>

        {/* Exam Instructions */}
        <div style={{ marginBottom: "20px", padding: "15px", border: "1px solid #ccc", borderRadius: "6px" }}>
          <h3 style={{ marginTop: 0 }}>Exam Instructions</h3>
          <ol style={{ lineHeight: "1.6" }}>
            <li>If the maximum number of warnings has been reached, any further violation will result in the automatic submission of your exam.</li>
            <li>Switching tabs is not allowed and may lead to automatic submission.</li>
            <li>Maintain a stable internet connection for the entire duration of the assessment.</li>
            <li>Avoid refreshing, closing, or navigating away from the exam window.</li>
          </ol>
        </div>

        {!examStarted ? (
          <div style={styles.preExamBox}>
            {remainingAttempts !== null && (
              <p>You have {remainingAttempts} out of 3 attempts remaining.</p>
            )}
            <button
              onClick={startExam}
              disabled={remainingAttempts === 0}
              style={{
                ...styles.btn,
                ...(remainingAttempts === 0
                  ? styles.btnDisabled
                  : styles.startBtn),
              }}
            >
              Start Exam
            </button>
          </div>

      ) : (
        <div style={styles.examLayout}>
          
          {/* LEFT PANEL */}
          <div style={styles.questionBox}>
            <div style={styles.headerRow}>
              <p style={styles.timer}>
                ⏳ Time Left: <strong>{formatTime(timeLeft)}</strong>
              </p>

              <label style={styles.reviewCheckbox}>
                <input
                  type="checkbox"
                  checked={
                    markForReview[questions[currentIndex]?.Question_ID] || false
                  }
                  onChange={() =>
                    handleMarkForReview(
                      questions[currentIndex].Question_ID
                    )
                  }
                />{" "}
                Mark for Review
              </label>
            </div>

            <h4>
              {currentIndex + 1}. {questions[currentIndex]?.Question_Text}
            </h4>

            <ul style={styles.optionList}>
              {questions[currentIndex]?.Options?.map((opt) => (
                <li key={opt.Answer_ID} style={styles.optionItem}>
                  <label>
                    <input
                      type="radio"
                      name={`q_${questions[currentIndex].Question_ID}`}
                      value={opt.Answer_ID}
                      checked={
                        answers[questions[currentIndex].Question_ID] ===
                        opt.Answer_ID
                      }
                      onChange={() =>
                        handleOptionChange(
                          questions[currentIndex].Question_ID,
                          opt.Answer_ID
                        )
                      }
                    />{" "}
                    {opt.Answer_Text}
                  </label>
                </li>
              ))}
            </ul>

            <div style={{ marginTop: 20 }}>
              <button
                style={{ ...styles.btn, marginRight: 10 }}
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((p) => p - 1)}
              >
                Previous
              </button>

              {currentIndex < questions.length - 1 ? (
                <button
                  style={styles.btn}
                  onClick={() => setCurrentIndex((p) => p + 1)}
                >
                  Next
                </button>
              ) : (
                <button
                  style={{ ...styles.btn, ...styles.submitBtn }}
                  onClick={submitExam}
                >
                  Submit Exam
                </button>
              )}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div style={styles.navigatorBox}>
            <h4>Question Navigator</h4>

            <div style={styles.questionGrid}>
              {questions.map((q, idx) => {
                const qStatus = questionStatuses[q.Question_ID];
                let bg = "#e57373";
                if (qStatus === "answered") bg = "#6fb471ff";
                else if (qStatus === "review") bg = "#ffc107";
                else if (idx === currentIndex) bg = "#2196f3";

                return (
                  <div
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    style={{
                      ...styles.questionButton,
                      backgroundColor: bg,
                      color: "white",
                    }}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>

            {/* STATUS */}
            <div style={styles.statusSummary}>
              <div
                style={{ ...styles.statusItem, backgroundColor: "#6fb471ff" }}
              >
                Answered: {answeredCount}
              </div>

              <div
                style={{
                  ...styles.statusItem,
                  backgroundColor: "#ffc107",
                  color: "#000",
                }}
              >
                Review: {reviewCount}
              </div>

              <div style={{ ...styles.statusItem, backgroundColor: "#2196f3" }}>
                Attempted: {attemptedCount}
              </div>

              <div style={{ ...styles.statusItem, backgroundColor: "#e57373" }}>
                Not Visited: {notVisitedCount}
              </div>
            </div>

            {/* LEGEND */}
            <div style={styles.legendBox}>
              <div style={styles.legendRow}>
                <div style={styles.legendItemLeft}> 🟩 Answered</div>
                <div style={styles.legendItemRight}>🟨 Review</div>
              </div>
              <div style={styles.legendRow}>
                <div style={styles.legendItemLeft}> 🔵 Current</div>
                <div style={styles.legendItemRight}>🟥 Not Visited</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------
// Styles
// -----------------------------
const styles = {
  container: {
    background: "#fff",
    minHeight: "100vh",
    fontFamily: "Segoe UI",
    padding: 20,
    color: "#333",
  },
  title: {
    color: "#E30016",
    fontSize: "2rem",
    textAlign: "center",
  },
  preExamBox: { textAlign: "center", marginTop: 50 },
  btn: {
    padding: "10px 18px",
    borderRadius: 5,
    fontSize: "1rem",
    cursor: "pointer",
    border: "none",
    transition: "0.3s",
  },
  btnDisabled: { backgroundColor: "#ccc", color: "#fff", cursor: "not-allowed" },
  startBtn: { backgroundColor: "#E30016", color: "#fff" },
  submitBtn: { backgroundColor: "#6E6F70", color: "#fff" },
  examLayout: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
  },
  questionBox: {
    flex: 3,
    padding: 20,
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    border: "1px solid #ddd",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  timer: { fontSize: "1rem" },
  reviewCheckbox: { fontWeight: "500" },
  navigatorBox: {
    flex: 1,
    padding: 15,
    backgroundColor: "#f1f1f1",
    borderRadius: 8,
    border: "1px solid #ccc",
    textAlign: "center",
  },
  questionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 30px)",
    gap: 6,
    justifyContent: "center",
    marginTop: 10,
  },
  questionButton: {
    width: 30,
    height: 30,
    borderRadius: 5,
    cursor: "pointer",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  optionList: { listStyle: "none", padding: 0, marginTop: 10 },
  optionItem: { marginBottom: 10 },
  statusSummary: {
    marginTop: 15,
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 8,
    justifyItems: "center",
  },
  statusItem: {
    color: "#fff",
    borderRadius: 6,
    padding: "4px 6px",
    fontWeight: "bold",
    fontSize: "13px",
    textAlign: "center",
    minWidth: "120px",
  },
  legendBox: {
    marginTop: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    fontSize: "13px",
    fontWeight: "500",
    width: "100%",
  },
  legendRow: {
    display: "flex",
    justifyContent: "space-between",
    width: "80%",
  },
  legendItemLeft: { textAlign: "left", flex: 1 },
  legendItemRight: { textAlign: "right", flex: 1 },
};

export default StudentExamPage;
